var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Children, isValidElement, memo, useContext } from "react";
import TableCell from "./TableCell";
import TableHeaderCell from "./TableHeaderCell";
import { isHeaderRow } from "./useTableDataWithHeaderRow";
import { actionsCellRenderer, actionsHeaderCellRenderer, isTableActionColumnRenderer, prefixId, } from "./TableActionColumn";
import { isTableActionColumn, isTableFillerColumn, isTableGroupingColumn, isTableSelectionColumn, TableDataContext, } from "./Table";
import flattenChildren from "../../utils/flattenChildren";
import { SELECTION_CELL_WIDTH } from "./TableSelectionColumn.style";
import { selectionCellRenderer, selectionHeaderCellRenderer } from "./TableSelectionColumn";
import { groupingCellRenderer, groupingHeaderCellRenderer } from "./TableGroupingColumn";
import { GROUPING_CELL_WIDTH, GROUPING_CELL_WITH_SELECTION_WIDTH } from "./TableGroupingColumn.style";
import get from "lodash/get";
import { useNxTranslation } from "../../i18n/useNxTranslation";
import useUniqueId from "../../hooks/useUniqueId";
export const extractTableConfigFromDataItem = (dataItem) => {
    const _a = dataItem, { _config } = _a, originalDataItem = __rest(_a, ["_config"]);
    return { tableConfig: _config, originalDataItem };
};
export const GridColumnCell = memo(function GridColumnCell(_a) {
    var _b, _c;
    var { id: gridCellPropsId } = _a, gridCellProps = __rest(_a, ["id"]);
    const { t, nxI18n } = useNxTranslation();
    const kebabId = useUniqueId(prefixId);
    const { tableConfig, originalDataItem } = extractTableConfigFromDataItem(gridCellProps.dataItem);
    // replace dataItem with config with the original dataItem
    gridCellProps.dataItem = originalDataItem;
    const tableColumn = gridCellProps.columnIndex !== undefined && tableConfig.columns[gridCellProps.columnIndex];
    const tableDataRef = useContext(TableDataContext);
    const data = tableDataRef === null || tableDataRef === void 0 ? void 0 : tableDataRef.current;
    if (!isValidElement(tableColumn)) {
        return null;
    }
    const defaultProps = {
        sortable: false,
        align: "left",
        whiteSpace: "nowrap",
    };
    let gridColumnProps;
    let shouldFitContent = false;
    const { actionMenuMaxHeight, actionMenuHandleQuickFind } = tableColumn.props;
    if (isTableActionColumn(tableColumn)) {
        const _d = tableColumn.props, { kebab, children: childrenOrRenderer, "data-insights": actionColInsights, actionMenuRenderer, hasActions } = _d, rest = __rest(_d, ["kebab", "children", "data-insights", "actionMenuRenderer", "hasActions"]);
        shouldFitContent = true;
        gridColumnProps = Object.assign(Object.assign(Object.assign({}, defaultProps), { locked: true, cellRenderer: (params) => {
                const actionsChildrenArray = isTableActionColumnRenderer(childrenOrRenderer)
                    ? flattenChildren(childrenOrRenderer(Object.assign(Object.assign({}, params), { data })))
                    : Children.toArray(childrenOrRenderer);
                const actions = actionsChildrenArray
                    .map((comp) => (isValidElement(comp) ? comp.props : undefined))
                    .filter(Boolean);
                return actionsCellRenderer(kebabId, actionMenuRenderer || actions, kebab, 
                // data-insights are based on TableActionColumn, but if not defined they'll be based on the prop from Table component
                actionColInsights || tableConfig.insightsAttr, actionMenuMaxHeight, actionMenuHandleQuickFind, hasActions)(Object.assign(Object.assign({}, params), { data }));
            }, headerCellRenderer: actionsHeaderCellRenderer(t, nxI18n), type: tableColumn.type.displayName }), rest);
    }
    else if (isTableSelectionColumn(tableColumn)) {
        const { selected, onRowSelection, onHeaderSelection, grouping, selectionMode } = tableColumn.props;
        gridColumnProps = Object.assign(Object.assign({}, defaultProps), { width: SELECTION_CELL_WIDTH, cellRenderer: selectionCellRenderer(selected, onRowSelection, tableConfig.id, grouping, tableConfig.dataItemKey, selectionMode), headerCellRenderer: selectionHeaderCellRenderer(tableConfig.id, selected, onHeaderSelection, t, nxI18n, tableConfig.insightsAttr, selectionMode), type: tableColumn.type.displayName });
    }
    else if (isTableGroupingColumn(tableColumn)) {
        const { grouping, onExpandToggle, isSelectionEnabled } = tableColumn.props;
        gridColumnProps = Object.assign(Object.assign({}, defaultProps), { cellRenderer: groupingCellRenderer(grouping, onExpandToggle), width: `${isSelectionEnabled ? GROUPING_CELL_WITH_SELECTION_WIDTH : GROUPING_CELL_WIDTH}px`, headerCellRenderer: groupingHeaderCellRenderer(t, nxI18n), type: tableColumn.type.displayName });
    }
    else if (isTableFillerColumn(tableColumn)) {
        gridColumnProps = Object.assign(Object.assign({}, defaultProps), { width: "auto", minWidth: "1px", type: tableColumn.type.displayName });
    }
    else {
        // Casting because Typescript was complaining with "does not exist on type 'never'"
        gridColumnProps = Object.assign(Object.assign({}, defaultProps), tableColumn.props);
    }
    const gridHeaderCellPropsId = (_c = (tableColumn.props.title || ((_b = tableColumn.key) === null || _b === void 0 ? void 0 : _b.toString()))) === null || _c === void 0 ? void 0 : _c.replace(/ /g, "_");
    return isHeaderRow(gridCellProps.dataItem) ? (React.createElement(TableHeaderCell, Object.assign({ id: `${tableConfig.id}_${gridHeaderCellPropsId}`, "data-insights": (tableConfig === null || tableConfig === void 0 ? void 0 : tableConfig.insightsAttr) && `${tableConfig.insightsAttr}_${gridHeaderCellPropsId}`, column: gridColumnProps, sort: tableConfig.sort, onSort: tableConfig.handleSortChange, tableLayout: tableConfig.tableLayout }, gridCellProps))) : (React.createElement(TableCell, Object.assign({ id: `${tableConfig.id}${gridCellPropsId}`, column: gridColumnProps, data: data, onDataChange: tableConfig.onDataChange, variant: tableConfig.variant, globalFilters: tableConfig.globalFilters, insightsAttr: (tableConfig === null || tableConfig === void 0 ? void 0 : tableConfig.insightsAttr) && `${tableConfig.insightsAttr}${gridCellPropsId}`, shouldFitContent: shouldFitContent, actionMenuMaxHeight: gridColumnProps.actionMenuMaxHeight, actionMenuHandleQuickFind: gridColumnProps.actionMenuHandleQuickFind, tableActionColumns: tableConfig.tableActionColumns }, gridCellProps)));
}, (oldProps, newProps) => {
    var _a, _b, _c;
    if (oldProps.columnsCount !== newProps.columnsCount) {
        return false;
    }
    if (oldProps.field !== newProps.field) {
        return false;
    }
    // use config of column and old/new prop values to determine whether to re-render
    const { tableConfig: oldTableConfig, originalDataItem: oldDataItem } = extractTableConfigFromDataItem(oldProps.dataItem);
    const oldColConfig = oldTableConfig === null || oldTableConfig === void 0 ? void 0 : oldTableConfig.columns[oldProps.columnIndex];
    const { tableConfig: newTableConfig, originalDataItem: newDataItem } = extractTableConfigFromDataItem(newProps.dataItem);
    const dataItemKey = newTableConfig === null || newTableConfig === void 0 ? void 0 : newTableConfig.dataItemKey;
    if (oldTableConfig.sort !== newTableConfig.sort) {
        return false;
    }
    if (oldTableConfig.dataLength !== newTableConfig.dataLength) {
        return false;
    }
    if (!isValidElement(oldColConfig)) {
        return false;
    }
    if (dataItemKey === undefined) {
        // if not dataItemKey then do not bother memoizing
        return false;
    }
    if (oldColConfig.props.cellRenderer) {
        // if cellRenderer specified for column we must re-render when data object changed in order to pass updated data object in params
        return false;
    }
    // if action column with action column renderer function we must re-render
    if (isTableActionColumn(oldColConfig) &&
        isTableActionColumnRenderer((_a = oldColConfig.props) === null || _a === void 0 ? void 0 : _a.children)) {
        // if action column with action column renderer function we must re-render
        return false;
    }
    if (isTableFillerColumn(oldColConfig)) {
        // table filler column cells never need re-rendering
        return true;
    }
    if (isTableSelectionColumn(oldColConfig)) {
        // always re-render selection column cells
        return false;
        // @TODO - further optimize to only re-render changed select item. Requires moving select state
    }
    if (isTableGroupingColumn(oldColConfig)) {
        // always re-render grouping column cells
        return false;
    }
    return (get(oldDataItem, (_b = oldColConfig === null || oldColConfig === void 0 ? void 0 : oldColConfig.props) === null || _b === void 0 ? void 0 : _b.field) ===
        get(newDataItem, (_c = oldColConfig === null || oldColConfig === void 0 ? void 0 : oldColConfig.props) === null || _c === void 0 ? void 0 : _c.field));
});
//# sourceMappingURL=GridColumnCell.js.map